import type { PaginatedItems } from '@api/hooks/common.ts';
import { toastError, toastSuccess } from '@components/Toast/Toast';
import { DEFAULT_PAGE_SIZE } from '@gurupay/gp-ui-library';
import { useAuth } from '@hooks/useAuth';
import apiClient from '@services/apiClient';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

export interface Device {
	id: string;
	platform?: 'ios' | 'android';
	device_name: string;
	app_version: string | null;
	last_registered_at: string;
}

export const DEVICES_PAGE_SIZE = DEFAULT_PAGE_SIZE;

export const useDevices = (currentPage: number, limit = DEVICES_PAGE_SIZE) => {
	const { session } = useAuth();

	const offset = currentPage === 1 ? 0 : (currentPage - 1) * limit;

	return useQuery({
		queryKey: ['devices', currentPage, limit],
		queryFn: async (): Promise<PaginatedItems<Device[]>> => {
			const response = await apiClient.get<PaginatedItems<Device[]>>('/users/devices', {
				params: { offset, limit },
			});
			return response.data;
		},
		enabled: !!session?.tokenized,
		refetchOnWindowFocus: false,
	});
};

export const useRemoveDevice = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation('', { keyPrefix: 'pushNotifications' });

	return useMutation({
		mutationFn: (deviceId: string) => apiClient.delete(`/users/devices/${deviceId}`),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['devices'] });
			toastSuccess(t('removeSuccess'));
		},
		onError: () => {
			toastError(t('removeError'));
		},
	});
};
